import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import TVCard from '@/components/base/TVCard';
import FAQSection from '@/components/base/FAQSection';
import { tvSeriesList, categories, faqList } from '@/mocks/tv-series';

export default function Home() {
  const hotSeries = tvSeriesList.slice(0, 8);
  const latestSeries = [...tvSeriesList].sort((a, b) => {
    if (a.status === '更新中' && b.status !== '更新中') return -1;
    if (a.status !== '更新中' && b.status === '更新中') return 1;
    return b.views - a.views;
  });

  const categoryIcons: Record<string, string> = {};
  categories.forEach((c) => {
    categoryIcons[c.slug] = c.icon;
  });

  return (
    <>
      <SEOHead
        title="小草影院-小草影院最新电视剧高清全集在线观看 | 小草影视热播剧集不缓冲追剧"
        description="小草影院提供最新电视剧高清全集在线观看，热播剧集每日更新，无需缓冲流畅追剧。涵盖小草影视热门电视剧、全集视频，高清画质即点即看，满足您完整追剧需求。"
        keywords="小草影院,小草影院最新电视剧,小草影视热播剧集,小草高清电视剧在线,小草影院全集视频,高清电视剧在线观看,电视剧全集免费,热播剧在线看,最新剧集不缓冲,小草影视追剧"
        canonicalUrl="https://www.xiaocaoyingyuan.com/"
        pageType="website"
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: '小草影院',
          url: 'https://www.xiaocaoyingyuan.com/',
          description: '小草影院提供最新电视剧高清全集在线观看，热播剧集每日更新，无需缓冲流畅追剧。',
          slogan: '小草虽小，视界无限',
        }}
      />
      <SchemaOrg
        type="WebSite"
        data={{
          name: '小草影院',
          url: 'https://www.xiaocaoyingyuan.com/',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.xiaocaoyingyuan.com/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '小草影院首页 - 最新电视剧高清全集在线观看',
          url: 'https://www.xiaocaoyingyuan.com/',
          description: '小草影院提供最新电视剧高清全集在线观看，热播剧集每日更新，无需缓冲流畅追剧。',
          isPartOf: { '@type': 'WebSite', name: '小草影院', url: 'https://www.xiaocaoyingyuan.com/' },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqList.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />

      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinema%20interior%20with%20red%20velvet%20curtains%20and%20golden%20stage%20lighting%2C%20empty%20theater%20seats%20with%20dramatic%20spotlight%2C%20luxurious%20movie%20theater%20atmosphere%2C%20warm%20gold%20and%20deep%20red%20tones%2C%20cinematic%20wide%20shot%2C%20elegant%20composition&width=1920&height=800&seq=home-hero-2025&orientation=landscape"
          alt="小草影院 - 最新电视剧高清全集在线观看"
          title="小草影院最新电视剧高清全集在线观看"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
            小草影院
          </h1>
          <p className="text-base md:text-xl text-white/80 max-w-2xl mb-2">
            最新电视剧高清全集在线观看，热播剧集每日更新
          </p>
          <p className="text-sm md:text-base text-white/60 max-w-xl">
            无需缓冲流畅追剧，涵盖小草影视热门电视剧，高清画质即点即看
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/category/all"
              className="px-8 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即追剧
            </Link>
            <Link
              to="/about"
              className="px-8 py-2.5 bg-white/10 text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            分类浏览
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-2 px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-200 transition-colors">
                <i className={`${cat.icon} text-sm`}></i>
              </div>
              <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              热门推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">大家都在追的热门好剧</p>
          </div>
          <Link
            to="/category/all"
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
          {hotSeries.map((series) => (
            <TVCard key={series.id} series={series} />
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                最新更新
              </h2>
              <p className="text-sm text-foreground-500 mt-1">热播剧集每日更新，追剧快人一步</p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
            {latestSeries.slice(0, 8).map((series) => (
              <TVCard key={series.id} series={series} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="lg:w-1/3 shrink-0">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 mb-4">
              关于小草影院的常见疑问，这里都有答案
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              联系我们
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </div>
            </Link>
          </div>
          <div className="lg:w-2/3">
            <FAQSection faqs={faqList.slice(0, 5)} />
          </div>
        </div>
      </section>
    </>
  );
}